import type { NextModuleRecommendationData } from '@tryhackme/interfaces';
import { store } from 'src/app/store';
import { RTKQueryTag } from 'src/common/constants';
import { decode } from 'src/common/helpers';
import type {
  AddWriteupMutation,
  ApiResponse,
  JoinRoomMutation,
  JoinRoomRequest,
  NetworkDiagramRoomData,
  ResetNetworkResponse,
  RoomCode,
  SetRoomVotesBody,
} from 'src/common/interfaces';
import type { Feedback } from 'src/common/interfaces/feedback';
import type { RunningInstance } from 'src/common/interfaces/instances';
import type { RoomNps, SubmitRoomNps } from 'src/common/interfaces/room-nps';
import type {
  GetScoreboardQueryParams,
  ScoreBoardTeamsResponse,
  ScoreBoardUsersResponse,
  ScoreboardUser,
} from 'src/common/interfaces/scoreboard';
import type { RoomTask } from 'src/common/interfaces/tasks';
import { apiSlice } from 'src/features/api/api-slice';
import type {
  PostRoomCompletionFeedbackByEcho,
  PostRoomCompletionFeedbackByEchoV2,
} from './components/congratulations-full-screen/components/post-room-completion-feedback-step';
import type {
  GetRoomTaskFilesRequestBody,
  GetRoomTaskFilesResponse,
} from './components/tasks/components/accordion/accordion.types';
import type {
  DownloadVpnConfig,
  RevealTicketValueData,
  RoomDetailsData,
  RoomVotesData,
  UserRoomScoreResponse,
} from './room.types';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNextModuleRecommendation: builder.query<ApiResponse<NextModuleRecommendationData>, string>({
      query: (roomCode) => ({
        url: 'modules/next-recommendation',
        params: { roomCode },
      }),
    }),
    getRoomDetail: builder.query<ApiResponse<RoomDetailsData>, string>({
      query: (roomCode) => `rooms/details?roomCode=${roomCode}`,
      providesTags: (_result, _error, roomCode) => [{ type: RTKQueryTag.Room, id: roomCode }],
    }),

    getRoomVotes: builder.query<ApiResponse<RoomVotesData>, string>({
      query: (roomCode) => `rooms/votes?roomCode=${roomCode}`,
    }),

    getRevealTicketValue: builder.query<ApiResponse<RevealTicketValueData>, string>({
      query: (roomCode) => `games/tickets/reveal?roomCode=${roomCode}`,
      providesTags: (_result, _error, roomCode) => [{ type: RTKQueryTag.Room, id: roomCode }],
    }),

    getRoomTasks: builder.query<ApiResponse<RoomTask[]>, string>({
      query: (roomCode) => ({ url: 'rooms/tasks', params: { roomCode } }),
      transformResponse: (response: ApiResponse<RoomTask[]>) => ({
        ...response,
        data:
          response?.data?.map((t: RoomTask) => ({
            ...t,
            questions: t.questions.map((q) => ({
              ...q,
              hint: decode(q.hint),
              progress: q.progress && {
                ...q.progress,
                submission: decode(q.progress.submission),
              },
            })),
          })) ?? [],
      }),
      providesTags: (_result, _error, roomCode) => [{ type: RTKQueryTag.Task, id: roomCode }],
    }),

    getRoomTaskFilesDownloadUrl: builder.query<ApiResponse<GetRoomTaskFilesResponse>, GetRoomTaskFilesRequestBody>({
      query: ({ roomId, downloadId, taskId }) => ({
        url: `rooms/tasks/download`,
        method: 'GET',
        params: { roomId, downloadId, taskId },
      }),
    }),

    getScoreboardData: builder.query<ApiResponse<ScoreboardUser[]>, GetScoreboardQueryParams>({
      query: ({ roomCode, limit, page, searchTerm }) => {
        const params = new URLSearchParams({ roomCode, limit: String(limit) });
        if (page) params.append('page', String(page));
        if (searchTerm) params.append('searchTerm', searchTerm);
        return {
          url: 'rooms/scoreboard',
          params,
        };
      },
      providesTags: (_result, _error, { roomCode }) => [{ type: RTKQueryTag.Scoreboard, id: roomCode }],
    }),

    getTopTeamsScoreboard: builder.query<
      ApiResponse<ScoreBoardTeamsResponse>,
      { roomCode: string; openChallenge?: boolean }
    >({
      query: ({ roomCode, openChallenge }) => ({
        url: 'rooms/scoreboard/top-teams',
        params: { roomCode, openChallenge },
      }),
      providesTags: (_result, _error, { roomCode }) => [{ type: RTKQueryTag.Scoreboard, id: roomCode }],
    }),

    getTopUsersScoreboard: builder.query<ApiResponse<ScoreBoardUsersResponse>, { roomCode: string }>({
      query: ({ roomCode }) => ({
        url: 'rooms/scoreboard/top-users',
        params: { roomCode },
      }),
      providesTags: (_result, _error, { roomCode }) => [{ type: RTKQueryTag.Scoreboard, id: roomCode }],
    }),

    getUserRoomScore: builder.query<ApiResponse<UserRoomScoreResponse>, string>({
      query: (roomCode) => ({
        url: 'rooms/user-score',
        params: { roomCode },
      }),
      providesTags: (result, error, roomCode) =>
        !error && result ? [{ type: RTKQueryTag.UserRoomScore, id: roomCode }] : [],
    }),

    setRoomVotes: builder.mutation<ApiResponse, SetRoomVotesBody>({
      query: ({ roomCode, vote }) => ({
        url: 'rooms/votes',
        method: 'POST',
        body: { roomCode, vote },
      }),
    }),

    leaveRoom: builder.mutation<ApiResponse, RoomCode>({
      query: ({ roomCode }) => ({
        url: 'rooms/leave',
        method: 'POST',
        body: { roomCode },
      }),
      invalidatesTags: (result, error, args) =>
        !error && result
          ? [
              { type: RTKQueryTag.Room, id: args.roomCode },
              { type: RTKQueryTag.Task, id: args.roomCode },
              { type: RTKQueryTag.Scoreboard, id: args.roomCode },
              { type: RTKQueryTag.UserRoomScore, id: args.roomCode },
              { type: RTKQueryTag.PostRoomCompletionFeedback, id: args.roomCode },
              { type: RTKQueryTag.UserRooms },
            ]
          : [],
    }),

    leaveRoomAsTeam: builder.mutation<ApiResponse, RoomCode>({
      query: ({ roomCode }) => ({
        url: 'rooms/leave-as-team',
        method: 'POST',
        body: { roomCode },
      }),
      invalidatesTags: (result, error, args) =>
        !error && result
          ? [
              { type: RTKQueryTag.Room, id: args.roomCode },
              { type: RTKQueryTag.Task, id: args.roomCode },
              { type: RTKQueryTag.Scoreboard, id: args.roomCode },
              { type: RTKQueryTag.UserRoomScore, id: args.roomCode },
              { type: RTKQueryTag.PostRoomCompletionFeedback, id: args.roomCode },
              { type: RTKQueryTag.UserRooms },
            ]
          : [],
    }),

    provideRoomCompletionFeedback: builder.mutation<ApiResponse, Omit<Feedback, 'userId' | 'date'>>({
      query: (body) => ({
        url: 'feedback',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error) => (!error && result ? [RTKQueryTag.UserFeedback] : []),
    }),

    resetUserProgress: builder.mutation<ApiResponse, RoomCode>({
      query: ({ roomCode }) => ({
        url: 'rooms/reset-progress',
        method: 'POST',
        body: { roomCode },
      }),
      invalidatesTags: (result, error, args) =>
        !error && result
          ? [
              { type: RTKQueryTag.Room, id: args.roomCode },
              { type: RTKQueryTag.Task, id: args.roomCode },
              { type: RTKQueryTag.Scoreboard, id: args.roomCode },
              { type: RTKQueryTag.UserRoomScore, id: args.roomCode },
              { type: RTKQueryTag.PostRoomCompletionFeedback, id: args.roomCode },
              { type: RTKQueryTag.PathsCurrentModule },
              { type: RTKQueryTag.RecentUserRooms },
              { type: RTKQueryTag.Hacktivities },
            ]
          : [],
    }),

    addWriteup: builder.mutation<ApiResponse, AddWriteupMutation>({
      query: ({ roomCode, title, link }) => ({
        url: 'rooms/writeups',
        method: 'POST',
        body: { roomCode, title, link },
      }),
      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.UserRooms, id: args.roomCode }] : [],
    }),

    joinRoom: builder.mutation<ApiResponse<true | JoinRoomMutation>, JoinRoomRequest>({
      query: ({ roomCode, pageReferrer, type }) => ({
        url: 'rooms/join',
        method: 'POST',
        body: { roomCode, pageReferrer, type },
      }),
      invalidatesTags: (result, error, args) =>
        !error && result
          ? [
              { type: RTKQueryTag.Room, id: args.roomCode },
              { type: RTKQueryTag.Task, id: args.roomCode },
              { type: RTKQueryTag.Scoreboard, id: args.roomCode },
              { type: RTKQueryTag.Networks },
              { type: RTKQueryTag.UserRoomScore, id: args.roomCode },
              { type: RTKQueryTag.PostRoomCompletionFeedback, id: args.roomCode },
              { type: RTKQueryTag.UserRooms },
              { type: RTKQueryTag.RecentUserRooms },
            ]
          : [],
    }),

    getRunning: builder.query<RunningInstance[], void>({
      query: () => `vms/running`,
      providesTags: () => [{ type: RTKQueryTag.RunningInstances }],
      transformResponse: (response: ApiResponse<RunningInstance[]>) => response?.data ?? [],
    }),

    terminateVm: builder.mutation<ApiResponse, string>({
      query: (instanceId) => ({
        url: 'vms/terminate',
        method: 'POST',
        body: { id: instanceId },
      }),
      invalidatesTags: [{ type: RTKQueryTag.RunningInstances }],
    }),

    downloadVpnConfig: builder.query<ApiResponse<DownloadVpnConfig>, string | undefined | null>({
      query: (networkId) => (networkId ? `vpns/config?networkId=${networkId}` : `vpns/config`),
    }),

    startNetwork: builder.mutation<ApiResponse<boolean>, string>({
      query: (networkId) => ({
        url: 'networks/start',
        method: 'POST',
        body: { id: networkId },
      }),
      invalidatesTags: (result, error) => (!error && result?.data ? [{ type: RTKQueryTag.Networks }] : []),
    }),

    resetNetwork: builder.mutation<ApiResponse<ResetNetworkResponse>, string>({
      query: (networkId) => ({
        url: 'networks/reset',
        method: 'POST',
        body: { id: networkId },
      }),
      invalidatesTags: (result, error) => (!error && result ? [{ type: RTKQueryTag.Networks }] : []),
    }),

    getNetwork: builder.query<NetworkDiagramRoomData | null, string>({
      query: (roomId) => ({ url: `networks/details`, params: { roomId } }),
      providesTags: (_result, _error, roomId) => [{ type: RTKQueryTag.Networks, id: roomId }],
      transformResponse: (response: ApiResponse<NetworkDiagramRoomData>) => response?.data ?? null,
    }),

    extendNetwork: builder.mutation<ApiResponse, string>({
      query: (networkId) => ({
        url: 'networks/extend',
        method: 'POST',
        body: { id: networkId },
      }),
      invalidatesTags: (result, error) => (!error && result ? [{ type: RTKQueryTag.Networks }] : []),
    }),

    getRoomNpsByUser: builder.query<RoomNps | null, string>({
      query: (roomCode) => `room-nps/recent?roomCode=${roomCode}`,
      transformResponse: (response: ApiResponse<RoomNps>) => response?.data ?? null,
      providesTags: (result, error, roomCode) =>
        !error && result ? [{ type: RTKQueryTag.RoomNpsSubmitted, id: roomCode }] : [],
    }),

    submitRoomNps: builder.mutation<
      ApiResponse<{
        success: boolean;
      }>,
      SubmitRoomNps
    >({
      query: (data) => ({
        url: 'room-nps',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) =>
        !error && result ? [{ type: RTKQueryTag.RoomNpsSubmitted, id: data.roomCode }] : [],
    }),

    getPostRoomCompletionFeedbackByEcho: builder.query<
      ApiResponse<PostRoomCompletionFeedbackByEcho | PostRoomCompletionFeedbackByEchoV2 | null>,
      string
    >({
      query: (roomCode) => ({
        url: 'rooms/post-room-completion-feedback-by-echo',
        params: { roomCode },
      }),
      providesTags: (result, error, roomCode) =>
        !error && result ? [{ type: RTKQueryTag.PostRoomCompletionFeedback, id: roomCode }] : [],
    }),

    voteRoomFeedback: builder.mutation<{ message: string }, { feedbackSectionId: string; feedback_value: 1 | -1 }>({
      query: ({ feedbackSectionId, feedback_value }) => ({
        url: `rooms/post-room-completion-feedback-by-echo/${feedbackSectionId}/votes`,
        method: 'POST',
        body: { feedback_value },
      }),
    }),

    revokeRoomFeedbackVote: builder.mutation<{ message: string }, { feedbackSectionId: string }>({
      query: ({ feedbackSectionId }) => ({
        url: `rooms/post-room-completion-feedback-by-echo/${feedbackSectionId}/votes/revoke`,
        method: 'POST',
      }),
    }),

    setActiveRoom: builder.mutation<ApiResponse, { roomCode: string; activeTaskNo?: number | null }>({
      query: ({ roomCode, activeTaskNo }) => ({
        url: 'rooms/set-active',
        method: 'POST',
        body: { roomCode, activeTaskNo },
      }),
    }),
  }),
});

export const invalidateCachedRunningVms = () => {
  store.dispatch(extendedApiSlice.util.invalidateTags([RTKQueryTag.RunningInstances]));
};

export const invalidateNetworkDetailsCache = () => {
  store.dispatch(extendedApiSlice.util.invalidateTags([RTKQueryTag.Networks]));
};

export const invalidateResetProgress = (roomCode: string) => {
  store.dispatch(
    extendedApiSlice.util.invalidateTags([
      { type: RTKQueryTag.Room },
      { type: RTKQueryTag.Task },
      { type: RTKQueryTag.Scoreboard },
      { type: RTKQueryTag.UserRoomScore },
      { type: RTKQueryTag.PostRoomCompletionFeedback, id: roomCode },
    ]),
  );
};

export const {
  useResetUserProgressMutation,
  useProvideRoomCompletionFeedbackMutation,
  useLeaveRoomMutation,
  useLeaveRoomAsTeamMutation,
  useGetRoomDetailQuery,
  useGetRoomVotesQuery,
  useSetRoomVotesMutation,
  useGetRoomTasksQuery,
  useLazyGetRoomTaskFilesDownloadUrlQuery,
  useGetRoomTaskFilesDownloadUrlQuery,
  useAddWriteupMutation,
  useJoinRoomMutation,
  useGetScoreboardDataQuery,
  useGetTopTeamsScoreboardQuery,
  useGetTopUsersScoreboardQuery,
  useGetUserRoomScoreQuery,
  useGetRunningQuery,
  useLazyGetRevealTicketValueQuery,
  useTerminateVmMutation,
  useLazyDownloadVpnConfigQuery,
  useResetNetworkMutation,
  useStartNetworkMutation,
  useGetNetworkQuery,
  useExtendNetworkMutation,
  useGetRoomNpsByUserQuery,
  useSubmitRoomNpsMutation,
  useGetPostRoomCompletionFeedbackByEchoQuery,
  useVoteRoomFeedbackMutation,
  useRevokeRoomFeedbackVoteMutation,
  useSetActiveRoomMutation,
  useGetNextModuleRecommendationQuery,
  endpoints: roomEndpoints,
} = extendedApiSlice;
